import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../context/AuthContext.js';

export default function Roadmap() {
  // Roadmap state
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [newRoadmapItem, setNewRoadmapItem] = useState({
    period: '',
    subject: '',
    description: ''
  });
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadRoadmapItems();
  }, []);

  // Roadmap management functions
  const loadRoadmapItems = async () => {
    try {
      const response = await axiosInstance.get('/settings/roadmap-items');
      setRoadmapItems(response.data);
    } catch (error) {
      console.error('Error loading roadmap items:', error);
    }
  };

  const addRoadmapItem = async () => {
    if (!newRoadmapItem.subject.trim()) {
      alert('Please enter a subject');
      return;
    }

    try {
      const response = await axiosInstance.post('/settings/roadmap-items', newRoadmapItem);
      setRoadmapItems([response.data, ...roadmapItems]);
      
      // Reset form
      setNewRoadmapItem({
        period: '',
        subject: '',
        description: ''
      });
    } catch (error) {
      console.error('Error adding roadmap item:', error);
      alert('Failed to add roadmap item');
    }
  };

  const startEditItem = (item) => {
    setEditingItemId(item._id);
    setEditingItem({ ...item });
  };

  const saveEditItem = async () => {
    try {
      const response = await axiosInstance.patch(`/settings/roadmap-items/${editingItemId}`, {
        period: editingItem.period,
        subject: editingItem.subject,
        description: editingItem.description
      });
      
      const updatedItems = roadmapItems.map(item =>
        item._id === editingItemId ? response.data : item
      );
      setRoadmapItems(updatedItems);
      setEditingItemId(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating roadmap item:', error);
      alert('Failed to update roadmap item');
    }
  };

  const cancelEdit = () => {
    setEditingItemId(null);
    setEditingItem(null);
  };

  const deleteRoadmapItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this roadmap item?')) {
      try {
        await axiosInstance.delete(`/settings/roadmap-items/${id}`);
        const updatedItems = roadmapItems.filter(item => item._id !== id);
        setRoadmapItems(updatedItems);
      } catch (error) {
        console.error('Error deleting roadmap item:', error);
        alert('Failed to delete roadmap item');
      }
    }
  };

  return (
    <div className="font-['Montserrat']">
      {/* Add New Roadmap Item Form */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6 bg-gray-50">
        <h4 className="text-lg font-semibold mb-3">Add New Roadmap Item</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
            <input
              type="text"
              value={newRoadmapItem.period}
              onChange={(e) => setNewRoadmapItem({ ...newRoadmapItem, period: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Q1 2024, Sprint 5, etc."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
            <input
              type="text"
              value={newRoadmapItem.subject}
              onChange={(e) => setNewRoadmapItem({ ...newRoadmapItem, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter feature or improvement subject"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={newRoadmapItem.description}
              onChange={(e) => setNewRoadmapItem({ ...newRoadmapItem, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Describe the feature or improvement"
            />
          </div>
        </div>
        <button
          onClick={addRoadmapItem}
          className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200 font-semibold"
        >
          Add Item
        </button>
      </div>

      {/* Roadmap Items List */}
      <div>
        <h4 className="text-lg font-semibold mb-3">Roadmap Items ({roadmapItems.length})</h4>
        {roadmapItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No roadmap items yet. Add your first item above!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {roadmapItems.map((item) => (
              <div key={item._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition duration-200">
                {editingItemId === item._id ? (
                  // Edit Mode
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
                        <input
                          type="text"
                          value={editingItem.period}
                          onChange={(e) => setEditingItem({ ...editingItem, period: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
                        <input
                          type="text"
                          value={editingItem.subject}
                          onChange={(e) => setEditingItem({ ...editingItem, subject: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editingItem.description}
                        onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={saveEditItem}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-1">
                          {item.period && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                              {item.period}
                            </span>
                          )}
                          <h5 className="text-lg font-semibold text-gray-800">{item.subject}</h5>
                        </div>
                      </div>
                    </div>
                    {item.description && (
                      <p className="text-gray-600 mb-3">{item.description}</p>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-400">
                        Added: {new Date(item.createdAt).toLocaleDateString()}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditItem(item)}
                          className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteRoadmapItem(item._id)}
                          className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

